import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ValidationError } from "@shared-core/data/adapter";
import { ConfigDefinitionEditor } from "../app/components/domain/admin/ConfigDefinitionEditor";
import { Button } from "../app/components/primitives/button";
import { Card, CardContent } from "../app/components/primitives/card";
import { Checkbox } from "../app/components/primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import { ArrowLeft, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { ExpressionField } from "../app/components/domain/admin/MultiExpressionEditor";
import { DefinitionAssistantPanel } from "../app/components/domain/admin/DefinitionAssistantPanel";
import { paths } from "@shared-core/routing/routes";
import { useSmartBack } from "../app/useSmartBack";

export function RecommendationEditorPage() {
  const dataAdapter = useDataAdapter();
  const { id } = useParams();
  const goBack = useSmartBack(paths.settingsRecommendations());
  const isEditMode = !!id;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [family, setFamily] = useState("");
  const [expressionFields, setExpressionFields] = useState<ExpressionField[]>(
    [],
  );
  const [status, setStatus] = useState<"live" | "inactive" | "draft">("draft");
  const [version, setVersion] = useState(1);
  // Per-edit intent: re-surface dismissed opportunities when this edit
  // supersedes an earlier version. Defaults off each edit.
  const [reopenDismissed, setReopenDismissed] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );

  // Track original state to detect changes
  const [originalState, setOriginalState] = useState<{
    name: string;
    family: string;
    expressionFields: ExpressionField[];
    status: "live" | "inactive" | "draft";
  } | null>(null);

  // Track if user has made any changes
  const isDirty =
    originalState !== null &&
    (name !== originalState.name ||
      family !== originalState.family ||
      JSON.stringify(expressionFields) !==
        JSON.stringify(originalState.expressionFields));

  // Track if status has changed
  const statusChanged =
    originalState !== null && status !== originalState.status;

  // Determine valid status options based on current state
  const getValidStatusOptions = (): Array<"live" | "inactive" | "draft"> => {
    if (!originalState) return ["draft", "live"];

    if (isDirty) {
      // When content is changed, can save as draft or live
      return ["draft", "live"];
    } else {
      // No content changes, only allow valid status transitions
      if (originalState.status === "live") {
        return ["live", "inactive"];
      } else if (originalState.status === "draft") {
        return ["draft", "live"];
      } else if (originalState.status === "inactive") {
        return ["inactive", "live"];
      }
    }
    return ["draft", "live", "inactive"];
  };

  // Check if current status change is valid
  function isValidStatusTransition(): boolean {
    if (!originalState || !statusChanged) return false;

    const validOptions = getValidStatusOptions();
    return validOptions.includes(status);
  }

  // Check if save should be enabled
  const hasValidChanges =
    isDirty || (statusChanged && isValidStatusTransition());

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogContent, setConfirmDialogContent] = useState<{
    title: string;
    description: string;
    action: () => void;
  } | null>(null);

  const [familyOptions, setFamilyOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [versionOptions, setVersionOptions] = useState<
    Array<{ version: number; timestamp: string }>
  >([]);
  const [savedExpressionFields, setSavedExpressionFields] = useState<
    ExpressionField[]
  >([]);

  // Get current live version info (if exists)
  // Assistant panel
  const [showAssistant, setShowAssistant] = useState(false);

  const [liveVersionInfo, setLiveVersionInfo] = useState<{
    version: number;
  } | null>(null);

  useEffect(() => {
    loadReferenceData();
    if (isEditMode && id) {
      loadDefinition(id);
    } else {
      // For new definitions, set the original state immediately
      setOriginalState({
        name: "",
        family: "",
        expressionFields: [],
        status: "draft",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load keyed on route id; load fns are recreated every render
  }, [id]);

  // Auto-flip to draft when editing a live version
  useEffect(() => {
    if (isDirty && originalState?.status === "live") {
      setStatus("draft");
      setVersion(0); // Clear version for new draft
    }
  }, [isDirty, originalState]);

  const loadReferenceData = async () => {
    const families = await dataAdapter.getRecommendationFamilyOptions();
    setFamilyOptions(families);
  };

  const loadDefinition = async (defId: string) => {
    setLoading(true);
    const def = await dataAdapter.getRecommendationDefinitionById(defId);
    if (def) {
      // Convert expressionFields to ExpressionField[] format
      let fields: ExpressionField[];
      if (def.expressionFields) {
        // New API format with granular expressions
        fields = [
          {
            key: "trigger_condition",
            label: "Trigger Condition",
            value: def.expressionFields.trigger_condition || "",
            placeholder: "Boolean expression for when to trigger",
          },
          {
            key: "title",
            label: "Title",
            value: def.expressionFields.title || "",
            placeholder: "Static title string",
          },
          {
            key: "rationale",
            label: "Rationale",
            value: def.expressionFields.rationale || "",
            placeholder: "Expression that returns rationale",
          },
          {
            key: "suggested_message",
            label: "Suggested Message",
            value: def.expressionFields.suggested_message || "",
            placeholder: "Expression that returns message template",
          },
          {
            key: "priority",
            label: "Priority",
            value: def.expressionFields.priority || "",
            placeholder: "Expression that returns priority",
          },
          {
            key: "due_in_days",
            label: "Due In Days",
            value: def.expressionFields.due_in_days || "",
            placeholder: "Expression that returns number of days",
          },
        ];
      } else {
        // Legacy: single expression field
        fields = [
          { key: "condition", label: "Condition", value: def.expression },
        ];
      }

      setName(def.name);
      setFamily(def.family);
      setExpressionFields(fields);
      setSavedExpressionFields(fields);
      setStatus(def.status);
      setVersion(def.version);

      // Set original state
      setOriginalState({
        name: def.name,
        family: def.family,
        expressionFields: fields,
        status: def.status,
      });

      const history = await dataAdapter.getRecommendationVersionHistory(defId);
      setVersionOptions(history);

      // Find live version if exists
      const allDefs = await dataAdapter.getRecommendationDefinitions({
        status: "live",
      });
      const liveDef = allDefs.items.find(
        (d) => d.name === def.name && d.status === "live",
      );
      if (liveDef) {
        setLiveVersionInfo({ version: liveDef.version });
      }
    }
    setLoading(false);
  };

  const handleChange = (field: string, value: any) => {
    setValidationErrors([]);

    switch (field) {
      case "name":
        setName(value);
        break;
      case "description":
        setDescription(value);
        break;
      case "family":
        setFamily(value);
        break;
      case "status":
        setStatus(value);
        break;
    }
  };

  const handleExpressionFieldChange = (key: string, value: string) => {
    setExpressionFields((prev) =>
      prev.map((field) => (field.key === key ? { ...field, value } : field)),
    );
  };

  const handleRevertAllExpressions = () => {
    if (savedExpressionFields) {
      setExpressionFields(savedExpressionFields);
    }
  };

  const handleVersionChange = async (newVersion: number) => {
    const history = await dataAdapter.getRecommendationVersionHistory(id!);
    const versionData = history.find((h) => h.version === newVersion);
    if (versionData) {
      setExpressionFields([
        { key: "condition", label: "Condition", value: versionData.expression },
      ]);
      setVersion(newVersion);
    }
  };

  const handleSave = async () => {
    // Validate: Prevent status-only change on live versions
    if (
      originalState?.status === "live" &&
      !isDirty &&
      status !== originalState.status
    ) {
      toast.error(
        "Cannot change status of a live version without making other changes",
      );
      setStatus(originalState.status); // Reset status
      return;
    }

    const errors: ValidationError[] = [];
    if (!name.trim()) {
      errors.push({ field: "name", message: "Name is required" });
    }
    if (!family) {
      errors.push({ field: "family", message: "Family is required" });
    }
    if (expressionFields.some((f) => !f.value.trim())) {
      errors.push({
        field: "expression",
        message: "All expression fields are required",
      });
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Determine save scenario and show confirmation
    if (!isEditMode) {
      // Creating new definition
      setConfirmDialogContent({
        title: "Create New Recommendation",
        description: "This will create version 1 as a draft.",
        action: performSave,
      });
    } else if (status === "live" && originalState?.status !== "live") {
      // Promoting to live
      const currentLiveVersion = liveVersionInfo?.version;
      setConfirmDialogContent({
        title: "Promote to Live",
        description: currentLiveVersion
          ? `This will promote version ${version || "new"} to live and replace the current live version (v${currentLiveVersion}), which will become inactive.`
          : `This will promote version ${version || "new"} to live.`,
        action: performSave,
      });
    } else if (status === "draft" || status === "inactive") {
      // Updating draft or inactive
      setConfirmDialogContent({
        title: isDirty ? "Save Changes" : "Update Status",
        description: isDirty
          ? `This will ${version ? `update version ${version}` : "create a new draft version"}. ${originalState?.status === "live" ? `The current live version will remain active.` : ""}`
          : `This will update the status to ${status}.`,
        action: performSave,
      });
    } else {
      // Fallback
      setConfirmDialogContent({
        title: "Save Changes",
        description: "This will save your changes.",
        action: performSave,
      });
    }

    setShowConfirmDialog(true);
  };

  const performSave = async () => {
    setShowConfirmDialog(false);
    setSaving(true);

    // Mock: Convert expressionFields back to single expression
    const expression =
      expressionFields.find((f) => f.key === "condition")?.value || "";

    const data = {
      name,
      family,
      description,
      expression,
      status,
      reopen_dismissed: reopenDismissed,
    };

    try {
      if (isEditMode && id) {
        const result = await dataAdapter.updateRecommendationDefinition(
          id,
          data,
        );
        if (result.success) {
          toast.success("Recommendation definition updated successfully");
          goBack();
        } else if (result.errors) {
          setValidationErrors(result.errors);
          toast.error("Validation failed");
        }
      } else {
        const result = await dataAdapter.createRecommendationDefinition(data);
        if (result.success) {
          toast.success("Recommendation definition created successfully");
          goBack();
        } else if (result.errors) {
          setValidationErrors(result.errors);
          toast.error("Validation failed");
        }
      }
    } catch {
      toast.error("An error occurred while saving");
    } finally {
      setSaving(false);
    }
  };

  // Declared before the loading early-return: React requires the same hook
  // order on every render, so all hooks must precede any conditional return.
  const [regeneratingDescription, setRegeneratingDescription] = useState(false);

  if (loading) {
    return (
      <div className="p-8">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const handleAcceptExpressions = (expressions: Record<string, string>) => {
    setExpressionFields((prev) =>
      prev.map((field) => {
        const newValue = expressions[field.key];
        return newValue !== undefined ? { ...field, value: newValue } : field;
      }),
    );
  };

  const handleAcceptHeader = (fields: {
    name?: string;
    family?: string;
    description?: string;
  }) => {
    if (fields.name !== undefined) setName(fields.name);
    if (fields.family !== undefined) setFamily(fields.family);
    if (fields.description !== undefined) setDescription(fields.description);
  };

  const handleRegenerateDescription = async () => {
    setRegeneratingDescription(true);
    try {
      const result = await dataAdapter.suggestDefinitionDescription({
        kind: "opportunity",
        name,
        family: family || null,
        currentDescription: description || null,
        expressions: Object.fromEntries(
          expressionFields.map((f) => [f.key, f.value]),
        ),
      });
      if (result.suggestedDescription) {
        setDescription(result.suggestedDescription);
        toast.success("Description regenerated");
      } else {
        toast.error("Could not generate a description — try again");
      }
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Description generation failed",
      );
    } finally {
      setRegeneratingDescription(false);
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <Button variant="ghost" onClick={goBack} className="mb-4">
            <ArrowLeft className="size-4 mr-2" />
            Back to Recommendations
          </Button>
          <h1 className="text-3xl mb-2">
            {isEditMode
              ? "Edit Recommendation Definition"
              : "Create Recommendation Definition"}
          </h1>
          <p className="text-muted-foreground">
            Configure Python expression to generate recommendations for customer
            success teams
          </p>
        </div>
        <Button
          variant={showAssistant ? "secondary" : "outline"}
          size="sm"
          onClick={() => setShowAssistant(!showAssistant)}
          className="mt-2"
        >
          {showAssistant ? (
            <>
              <X className="size-4 mr-1.5" />
              Close Assistant
            </>
          ) : (
            <>
              <Sparkles className="size-4 mr-1.5" />
              AI Assistant
            </>
          )}
        </Button>
      </div>

      <div className={showAssistant ? "flex gap-6" : ""}>
        <div className={showAssistant ? "flex-1 min-w-0" : ""}>
          <Card>
            <CardContent className="pt-6">
              <ConfigDefinitionEditor
                type="recommendation"
                name={name}
                family={family}
                description={description}
                expressionFields={expressionFields}
                status={status}
                version={version}
                familyOptions={familyOptions}
                versionOptions={isEditMode ? versionOptions : undefined}
                validationErrors={validationErrors}
                savedExpressionFields={savedExpressionFields}
                validStatusOptions={getValidStatusOptions()}
                onChange={handleChange}
                onExpressionFieldChange={handleExpressionFieldChange}
                onRevertAllExpressions={handleRevertAllExpressions}
                onVersionChange={isEditMode ? handleVersionChange : undefined}
                onRegenerateDescription={handleRegenerateDescription}
                isRegeneratingDescription={regeneratingDescription}
              />
            </CardContent>
          </Card>

          <label className="flex items-start gap-2 mt-6 text-sm text-muted-foreground cursor-pointer max-w-2xl">
            <Checkbox
              checked={reopenDismissed}
              onCheckedChange={(checked) =>
                setReopenDismissed(Boolean(checked))
              }
              id="reopen-dismissed"
              className="mt-0.5"
            />
            <span>
              Re-surface dismissed opportunities — this change is material
              enough that opportunities users already dismissed should reopen on
              the next run. Leave unticked for minor tweaks. Linked tickets are
              never affected.
            </span>
          </label>

          <div className="flex gap-3 mt-6">
            <Button onClick={handleSave} disabled={saving || !hasValidChanges}>
              {saving
                ? "Saving..."
                : isEditMode
                  ? "Update Recommendation"
                  : "Create Recommendation"}
            </Button>
            <Button variant="outline" onClick={goBack}>
              Cancel
            </Button>
          </div>
        </div>

        {showAssistant && (
          <div className="w-[380px] flex-shrink-0">
            <Card className="h-[600px]">
              <DefinitionAssistantPanel
                definitionType="opportunity"
                currentExpressions={Object.fromEntries(
                  expressionFields.map((f) => [f.key, f.value]),
                )}
                onAcceptExpressions={handleAcceptExpressions}
                onAcceptHeader={handleAcceptHeader}
              />
            </Card>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmDialogContent?.title}</DialogTitle>
            <DialogDescription>
              {confirmDialogContent?.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={confirmDialogContent?.action}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
